/**
 * @module engine3d/gizmo
 * @summary The drag handles: a three-axis mover for equipment and a re-route
 *   handle for pipe runs.
 * @description
 *   Handles are built as ordinary triangle geometry and drawn in a separate
 *   OVERLAY pass after the plant. That is deliberate: sorted into the scene
 *   they would disappear inside the machine they belong to, which is exactly
 *   when you need them.
 *
 *   They are also sized in SCREEN space — the geometry is scaled by the
 *   camera's pixels-per-unit each rebuild — so a handle on a valve is as easy
 *   to grab as one on a silo, and neither changes size as you zoom.
 *
 *   Dragging resolves through `math3` ray intersections: an axis handle takes
 *   the closest point between the mouse ray and its axis line, a plane handle
 *   takes the ray's intersection with the plane it slides in. Both are exact,
 *   so the handle stays under the cursor instead of drifting.
 */
import type { Connection, Shape } from '../types';
import { S } from '../engine/state';
import { Camera } from './camera';
import { rayAxisPoint, rayPlanePoint, v3, type Vec3 } from './math3';
import { MAT_KEYS } from './scene';
import type { MatKey } from './materials';
import { cylinder, frustum, boxCentered, merge, rotateX, rotateZ, translate, type Mesh } from './mesh';
import { placeOf } from './placement';
import { endsOf, portWorld, routePipe3 } from './pipes';
import { editableVertices, findPipe3, legMidpoints, radiusOf } from './pipes3';
import { editableEnds, findDim3 } from './dims3';
import { filletPath, sphere, torusArc, tube } from './mesh';
import { getModelFor } from './models';

const MAT_INDEX: Record<string, number> = {};
MAT_KEYS.forEach((k, i) => (MAT_INDEX[k] = i));

/** What a handle edits and how it is constrained. */
export type HandleKind = 'x' | 'y' | 'z' | 'yaw' | 'plan' | 'pipe' | 'wp' | 'leg';

/**
 * Object index used for geometry that is drawn but not grabbable — nozzle
 * markers and the draft run. It is deliberately out of range of the handle
 * list, so a click that lands on one resolves to no handle at all.
 */
export const DECOR = 60000;

/**
 * Dimension handle proportions.
 *
 * A dimension carries SIX arrows - three axes at each end - so they are kept
 * below the equipment mover's size. Not much below: shrinking them far enough
 * to be unobtrusive also makes them fiddly to grab, and a handle you cannot hit
 * is worse than one that is slightly in the way.
 */
/**
 * Yaw ring proportions, as multiples of the axis arm.
 *
 * The ring sits OUTSIDE the arrow tips. Putting it inside would have it cross
 * the shafts in screen space, and then a click in the overlap is a coin toss
 * between moving the machine and spinning it — the two things you least want
 * to confuse.
 */
const RING_R = 1.24;
const RING_TUBE = 0.85;

const DIM_ARM = 0.5;
const DIM_SHAFT = 0.75;
const DIM_HUB = 0.72;
const DIM_CUBE = 0.95;

export interface HandleDesc {
  kind: HandleKind;
  /** Shape id, or connection id for a pipe or bend handle. */
  targetId: string;
  /** World origin of the gizmo this handle belongs to. */
  origin: Vec3;
  /** Constraint axis for `x` / `y` / `z`; the offset axis for `pipe`. */
  axis: Vec3;
  /**
   * Index into a connection's `wp3`, or into a 3D pipe's vertices, when this
   * handle moves a BEND rather than equipment. The axis handles are shared
   * between all three — only the thing they write to differs.
   */
  wp?: number;
  /** Which list `wp` indexes. Absent means a 2D connector's bends. */
  owner?: 'conn' | 'pipe3' | 'dim';
}

/** Everything the gizmo builder needs to know about the current interaction. */
export interface GizmoContext {
  /** Index of the handle under the cursor, highlighted. */
  hovered: number;
  /** Present while the pipe tool is running. */
  drawing?: {
    /** Vertices committed so far. */
    pts: Vec3[];
    /** Where the cursor currently is, already snapped. */
    preview: Vec3 | null;
    /** Radius of the run being drawn. */
    radius: number;
  };
}

/** A small triangle buffer drawn on top of the plant. */
export interface Overlay {
  pos: Float32Array;
  nrm: Float32Array;
  mat: Uint8Array;
  obj: Uint16Array;
  count: number;
  handles: HandleDesc[];
}

export const emptyOverlay = (): Overlay => ({
  pos: new Float32Array(0),
  nrm: new Float32Array(0),
  mat: new Uint8Array(0),
  obj: new Uint16Array(0),
  count: 0,
  handles: [],
});

// ─── Building ────────────────────────────────────────────────────────────

class Builder {
  pos: number[] = [];
  nrm: number[] = [];
  mat: number[] = [];
  obj: number[] = [];

  add(m: Mesh, matIdx: number, handleIdx: number): void {
    const tris = m.idx.length / 3;
    for (let t = 0; t < tris; t++) {
      for (let k = 0; k < 3; k++) {
        const vi = m.idx[t * 3 + k] * 3;
        this.pos.push(m.pos[vi], m.pos[vi + 1], m.pos[vi + 2]);
        this.nrm.push(m.nrm[vi], m.nrm[vi + 1], m.nrm[vi + 2]);
      }
      this.mat.push(matIdx);
      this.obj.push(handleIdx);
    }
  }

  finish(handles: HandleDesc[]): Overlay {
    return {
      pos: new Float32Array(this.pos),
      nrm: new Float32Array(this.nrm),
      mat: new Uint8Array(this.mat),
      obj: new Uint16Array(this.obj),
      count: this.mat.length,
      handles,
    };
  }
}

/** An arrow along +Y of length `len`, ready to be rotated onto another axis. */
function arrow(len: number, r: number): Mesh {
  return merge(cylinder(r, len * 0.78, { seg: 10 }), translate(frustum(r * 2.4, 0, len * 0.24, { seg: 10 }), 0, len * 0.76, 0));
}

/** The same arrow aimed down +X, +Y or +Z. */
function axisArrow(kind: 'x' | 'y' | 'z', len: number, r: number, at: Vec3): Mesh {
  const m = arrow(len, r);
  if (kind === 'x') rotateZ(m, -Math.PI / 2);
  else if (kind === 'z') rotateX(m, Math.PI / 2);
  return translate(m, at.x, at.y, at.z);
}

/**
 * Build the handles, nozzle markers and draft preview for the current state.
 *
 * Everything here is sized from the camera's pixels-per-unit, so a handle on a
 * valve is as easy to grab as one on a silo and neither changes size as you
 * zoom.
 */
export function buildGizmo(cam: Camera, viewportH: number, ctx: GizmoContext): Overlay {
  const hovered = ctx.hovered;
  const ids = S.selected;
  const drawing = ctx.drawing;
  if (!ids.length && !drawing) return emptyOverlay();

  const ppu = cam.pixelScale(viewportH);
  // ~58 px arms, ~9 px hubs, whatever the zoom.
  const L = ppu * 58;
  const R = ppu * 2.6;
  const HUB = ppu * 7;

  const b = new Builder();
  const handles: HandleDesc[] = [];
  const matOf = (m: MatKey, idx: number) => MAT_INDEX[hovered === idx ? 'gizmoHot' : m];

  const shapes = ids.map((id) => S.shapes.find((s) => s.id === id)).filter(Boolean) as Shape[];
  const conns = ids.map((id) => S.connections.find((c) => c.id === id)).filter(Boolean) as Connection[];
  const pipes = ids.map((id) => findPipe3(id)).filter(Boolean) as NonNullable<ReturnType<typeof findPipe3>>[];
  const dims = ids.map((id) => findDim3(id)).filter(Boolean) as NonNullable<ReturnType<typeof findDim3>>[];

  // ── Pipe tool: every nozzle you could join, plus the run being drawn ──
  if (drawing) {
    const seen = new Set<string>();
    const marks: Mesh[] = [];
    for (const s of S.shapes) {
      if (s.type === 'zonebox' || s.type === 'textlabel' || s.type === 'processarrow') continue;
      const faces: Vec3[] = [];
      for (const k of ['N', 'S', 'E', 'W'] as const) {
        const wp = portWorld(s, k);
        // Elbows alias two cardinals onto each real face; mark each face once.
        if (faces.some((q) => Math.hypot(q.x - wp.p.x, q.y - wp.p.y, q.z - wp.p.z) < 0.5)) continue;
        faces.push(wp.p);
        const key = `${Math.round(wp.p.x)}|${Math.round(wp.p.y)}|${Math.round(wp.p.z)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        marks.push(translate(sphere(ppu * 4, 8, 5), wp.p.x, wp.p.y, wp.p.z));
      }
    }
    if (marks.length) b.add(merge(...marks), MAT_INDEX.gizmoY, DECOR);

    const line = drawing.preview ? [...drawing.pts, drawing.preview] : drawing.pts;
    if (line.length >= 2) {
      b.add(tube(filletPath(line, drawing.radius * 2.4, 4), drawing.radius, 10), MAT_INDEX.gizmoPipe, DECOR);
    }
    for (const q of line) b.add(translate(sphere(ppu * 3.4, 8, 5), q.x, q.y, q.z), MAT_INDEX.gizmoHot, DECOR);
    // While drawing, the equipment gizmo would only be in the way.
    return b.finish(handles);
  }

  // ── Equipment mover ──
  if (shapes.length) {
    // One gizmo for the whole selection, anchored on its centre so a multi-item
    // drag has a single obvious grip.
    let cx = 0;
    let cz = 0;
    let cy = 0;
    for (const s of shapes) {
      const p = placeOf(s);
      cx += p.x;
      cz += p.z;
      cy = Math.max(cy, p.y + getModelFor(s).height);
    }
    cx /= shapes.length;
    cz /= shapes.length;
    const origin = v3(cx, cy + ppu * 16, cz);

    const axes: [HandleKind & ('x' | 'y' | 'z'), Vec3, MatKey][] = [
      ['y', v3(0, 1, 0), 'gizmoY'],
      ['x', v3(1, 0, 0), 'gizmoX'],
      ['z', v3(0, 0, 1), 'gizmoZ'],
    ];
    for (const [kind, axis, mat] of axes) {
      const idx = handles.length;
      handles.push({ kind, targetId: shapes[0].id, origin, axis });
      b.add(axisArrow(kind, L, R, origin), matOf(mat, idx), idx);
    }
    // Centre hub: free movement across the floor.
    const planIdx = handles.length;
    handles.push({ kind: 'plan', targetId: shapes[0].id, origin, axis: v3(0, 1, 0) });
    b.add(translate(boxCentered(HUB, HUB, HUB), origin.x, origin.y, origin.z), matOf('gizmoHot', planIdx), planIdx);

    // Yaw ring: spin about the vertical axis, in the 3D view only. It is green
    // like the Y arrow because that is the axis it turns about — the same
    // grammar every modelling tool uses, so it needs no explaining.
    const yawIdx = handles.length;
    handles.push({ kind: 'yaw', targetId: shapes[0].id, origin, axis: v3(0, 1, 0) });
    b.add(
      translate(torusArc(L * RING_R, R * RING_TUBE, 0, Math.PI * 2, 44, 7), origin.x, origin.y, origin.z),
      matOf('gizmoY', yawIdx),
      yawIdx,
    );
  }

  // ── Pipe re-router ──
  if (conns.length) {
    const byId = new Map<string, Shape>();
    S.shapes.forEach((s) => byId.set(s.id, s));
    for (const cn of conns) {
      const ends = endsOf(cn, byId);
      if (!ends) continue;
      const info = routePipe3(ends[0], ends[1], cn);

      if (info.waypoints.length) {
        // Hand-shaped run: every bend gets its own three-axis mover, at 60%
        // size so a run with three of them stays readable.
        info.waypoints.forEach((wp, wi) => {
          const axes: [('x' | 'y' | 'z'), Vec3, MatKey][] = [
            ['y', v3(0, 1, 0), 'gizmoY'],
            ['x', v3(1, 0, 0), 'gizmoX'],
            ['z', v3(0, 0, 1), 'gizmoZ'],
          ];
          for (const [kind, axis, mat] of axes) {
            const idx = handles.length;
            handles.push({ kind, targetId: cn.id, origin: wp, axis, wp: wi });
            b.add(axisArrow(kind, L * 0.6, R * 0.85, wp), matOf(mat, idx), idx);
          }
          const hubIdx = handles.length;
          handles.push({ kind: 'wp', targetId: cn.id, origin: wp, axis: v3(0, 1, 0), wp: wi });
          b.add(
            translate(boxCentered(HUB * 1.05, HUB * 1.05, HUB * 1.05), wp.x, wp.y, wp.z),
            matOf('gizmoPipe', hubIdx),
            hubIdx,
          );
        });
        continue;
      }

      // Automatic run: one handle on the crossing leg.
      const idx = handles.length;
      const axis = info.axis === 'x' ? v3(1, 0, 0) : v3(0, 0, 1);
      handles.push({ kind: 'pipe', targetId: cn.id, origin: info.handle, axis });
      const h = info.handle;
      b.add(
        merge(
          translate(boxCentered(HUB * 1.15, HUB * 1.15, HUB * 1.15), h.x, h.y, h.z),
          // Little cross of arrows showing the two directions it moves in.
          axisArrow('y', L * 0.5, R * 0.7, v3(h.x, h.y + HUB * 0.6, h.z)),
          info.axis === 'x'
            ? axisArrow('x', L * 0.5, R * 0.7, v3(h.x + HUB * 0.6, h.y, h.z))
            : axisArrow('z', L * 0.5, R * 0.7, v3(h.x, h.y, h.z + HUB * 0.6)),
        ),
        matOf('gizmoPipe', idx),
        idx,
      );
    }
  }

  // ── Vertices of a hand-drawn 3D pipe ──
  for (const q of pipes) {
    const r = radiusOf(q);
    for (const v of editableVertices(q)) {
      const axes: [('x' | 'y' | 'z'), Vec3, MatKey][] = [
        ['y', v3(0, 1, 0), 'gizmoY'],
        ['x', v3(1, 0, 0), 'gizmoX'],
        ['z', v3(0, 0, 1), 'gizmoZ'],
      ];
      for (const [kind, axis, mat] of axes) {
        const idx = handles.length;
        handles.push({ kind, targetId: q.id, origin: v.p, axis, wp: v.index, owner: 'pipe3' });
        b.add(axisArrow(kind, L * 0.55, R * 0.8, v.p), matOf(mat, idx), idx);
      }
      const hubIdx = handles.length;
      handles.push({ kind: 'wp', targetId: q.id, origin: v.p, axis: v3(0, 1, 0), wp: v.index, owner: 'pipe3' });
      b.add(
        translate(boxCentered(HUB, HUB, HUB), v.p.x, v.p.y, v.p.z),
        matOf('gizmoPipe', hubIdx),
        hubIdx,
      );
      void r;
    }

    // One delete marker per leg. Clicking it cuts the run there rather than
    // deleting the whole thing — which is the only way to remove the piece
    // between two particular bends.
    for (const leg of legMidpoints(q)) {
      const idx = handles.length;
      handles.push({ kind: 'leg', targetId: q.id, origin: leg.p, axis: v3(0, 1, 0), wp: leg.index, owner: 'pipe3' });
      b.add(
        merge(
          translate(sphere(HUB * 0.62, 8, 5), leg.p.x, leg.p.y, leg.p.z),
          // A short bar across it, so it reads as "cut here" and not as another
          // grab handle.
          translate(boxCentered(HUB * 1.5, HUB * 0.24, HUB * 0.24), leg.p.x, leg.p.y, leg.p.z),
        ),
        matOf('gizmoX', idx),
        idx,
      );
    }
  }

  // ── Ends of a hand-placed dimension ──
  // Both ends get handles, anchored or not: a dimension pinned to the wrong
  // nozzle has to be movable, and grabbing an anchored end detaches it.
  for (const d of dims) {
    for (const end of editableEnds(d)) {
      const axes: [('x' | 'y' | 'z'), Vec3, MatKey][] = [
        ['y', v3(0, 1, 0), 'gizmoY'],
        ['x', v3(1, 0, 0), 'gizmoX'],
        ['z', v3(0, 0, 1), 'gizmoZ'],
      ];
      for (const [kind, axis, mat] of axes) {
        const idx = handles.length;
        handles.push({ kind, targetId: d.id, origin: end.p, axis, wp: end.index, owner: 'dim' });
        b.add(axisArrow(kind, L * DIM_ARM, R * DIM_SHAFT, end.p), matOf(mat, idx), idx);
      }
      const hubIdx = handles.length;
      handles.push({ kind: 'wp', targetId: d.id, origin: end.p, axis: v3(0, 1, 0), wp: end.index, owner: 'dim' });
      b.add(
        // A pinned end is drawn as a ball, a free one as a cube — so you can
        // see at a glance which ends will follow their equipment.
        end.anchored
          ? translate(sphere(HUB * DIM_HUB, 10, 6), end.p.x, end.p.y, end.p.z)
          : translate(boxCentered(HUB * DIM_CUBE, HUB * DIM_CUBE, HUB * DIM_CUBE), end.p.x, end.p.y, end.p.z),
        matOf('gizmoHot', hubIdx),
        hubIdx,
      );
    }
  }

  return b.finish(handles);
}

// ─── Drag resolution ─────────────────────────────────────────────────────

/** Re-exported so callers have one place to reach the drag maths. */
export { rayAxisPoint, rayPlanePoint };

/**
 * Where a handle's grip currently is, given a mouse ray.
 *
 * `plan` slides in the horizontal plane through the gizmo. `pipe` slides in the
 * VERTICAL plane containing its offset axis, so one drag adjusts both the run
 * elevation and its sideways position — which is how the 2D segment handles
 * already behave, one dimension up.
 */
export function handlePoint(h: HandleDesc, o: Vec3, d: Vec3): Vec3 | null {
  switch (h.kind) {
    case 'x':
    case 'y':
    case 'z':
      return rayAxisPoint(o, d, h.origin, h.axis);
    case 'leg':
      // Leg markers are clicked, never dragged.
      return null;
    case 'yaw':
      // The ring lies in the horizontal plane through the gizmo, so the grip is
      // wherever the pointer crosses that plane. The ANGLE is then taken from
      // the grip about the origin — see the yaw case in the drag handler.
      return rayPlanePoint(o, d, h.origin, v3(0, 1, 0));
    case 'plan':
    case 'wp':
      // The bend hub slides across the horizontal plane it sits in; its arrows
      // handle height. Same grammar as the equipment mover.
      return rayPlanePoint(o, d, h.origin, v3(0, 1, 0));
    case 'pipe': {
      // Plane normal is the horizontal axis the run does NOT move along.
      const n = h.axis.x !== 0 ? v3(0, 0, 1) : v3(1, 0, 0);
      const hit = rayPlanePoint(o, d, h.origin, n);
      if (hit) return hit;
      // Edge-on to that plane: fall back to the vertical axis alone rather than
      // dropping the drag entirely.
      return rayAxisPoint(o, d, h.origin, v3(0, 1, 0));
    }
  }
}
